import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, MapPin, ExternalLink } from "lucide-react";
import {
  inputCls,
  AREA_UNITS,
  OWNERSHIP_TYPES,
  fmtNumber,
  buildMapHref,
} from "./shared";
import Field from "./Field";
import DocumentList from "./DocumentList";

const empty = {
  name: "",
  survey_number: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
  latitude: "",
  longitude: "",
  total_area: 0,
  area_unit: "sqft",
  ownership_type: "Owned",
  purchase_date: "",
  purchase_value: 0,
  documents: [],
  notes: "",
  is_active: true,
};

export default function LandsTab({ lands, refresh, writable }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (l) => {
    setEditing(l);
    setForm({
      ...empty,
      ...l,
      purchase_date: l.purchase_date || "",
      documents: (l.documents || []).map((d) => ({ ...d })),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Land name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        total_area: Number(form.total_area) || 0,
        purchase_value: Number(form.purchase_value) || 0,
        purchase_date: form.purchase_date || null,
      };
      if (editing) {
        await api.put(`/lands/${editing.id}`, payload);
        toast.success("Land updated");
      } else {
        await api.post(`/lands`, payload);
        toast.success("Land added");
      }
      setOpen(false);
      refresh();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const l = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/lands/${l.id}`);
      toast.success("Land removed");
      refresh();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed",
      );
    }
  };

  return (
    <div data-testid="lands-tab">
      <div className="flex justify-end mb-4">
        {writable && (
          <button
            onClick={openCreate}
            data-testid="land-add-button"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm"
          >
            <Plus size={14} strokeWidth={1.5} /> Add Land
          </button>
        )}
      </div>
      {lands.length === 0 ? (
        <div className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center">
          <MapPin size={28} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-3 font-serif text-2xl text-[#1c1a17]">
            No land records yet
          </div>
          <div className="mt-2 text-sm text-[#6b655b]">
            Add your first land record to begin building the property hierarchy.
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="lands-table">
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {[
                  "Name",
                  "Survey No.",
                  "Location",
                  "Area",
                  "Ownership",
                  "Map",
                  "Docs",
                  "",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lands.map((l) => {
                const mapHref = buildMapHref(
                  l.latitude,
                  l.longitude,
                  [l.address1, l.city, l.state].filter(Boolean).join(", "),
                );
                return (
                  <tr
                    key={l.id}
                    data-testid={`land-row-${l.id}`}
                    className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                  >
                    <td className="px-4 py-3 text-[#1c1a17]">{l.name}</td>
                    <td className="px-4 py-3 text-[#6b655b] font-mono text-[12px]">
                      {l.survey_number || "—"}
                    </td>
                    <td className="px-4 py-3 text-[#6b655b]">
                      {[l.city, l.state].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-[#6b655b]">
                      {fmtNumber(l.total_area)} {l.area_unit || ""}
                    </td>
                    <td className="px-4 py-3 text-[#6b655b]">
                      {l.ownership_type || "—"}
                    </td>
                    <td className="px-4 py-3">
                      {mapHref ? (
                        <a
                          href={mapHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`land-map-${l.id}`}
                          className="text-[#234333] inline-flex items-center gap-1 text-xs hover:underline"
                        >
                          View <ExternalLink size={10} strokeWidth={1.5} />
                        </a>
                      ) : (
                        <span className="text-xs text-[#6b655b]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#6b655b] text-xs">
                      {(l.documents || []).length || 0}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(l)}
                        data-testid={`land-edit-${l.id}`}
                        className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                      >
                        <Pencil size={14} strokeWidth={1.5} />{" "}
                        {writable ? "Edit" : "View"}
                      </button>
                      {writable && (
                        <button
                          onClick={() => setConfirmDelete(l)}
                          data-testid={`land-delete-${l.id}`}
                          className="ml-2 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                        >
                          <Trash2 size={14} strokeWidth={1.5} /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="land-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Land" : "Add Land"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Land Name *">
              <input
                data-testid="land-name-input"
                className={inputCls}
                value={form.name}
                disabled={!writable}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label="Survey Number">
              <input
                data-testid="land-survey-input"
                className={inputCls}
                value={form.survey_number}
                disabled={!writable}
                onChange={(e) => set("survey_number", e.target.value)}
              />
            </Field>
            <Field label="Address Line 1" full>
              <input
                className={inputCls}
                value={form.address1}
                disabled={!writable}
                onChange={(e) => set("address1", e.target.value)}
              />
            </Field>
            <Field label="Address Line 2" full>
              <input
                className={inputCls}
                value={form.address2}
                disabled={!writable}
                onChange={(e) => set("address2", e.target.value)}
              />
            </Field>
            <Field label="City">
              <input
                className={inputCls}
                value={form.city}
                disabled={!writable}
                onChange={(e) => set("city", e.target.value)}
              />
            </Field>
            <Field label="State">
              <input
                className={inputCls}
                value={form.state}
                disabled={!writable}
                onChange={(e) => set("state", e.target.value)}
              />
            </Field>
            <Field label="Pincode">
              <input
                className={inputCls}
                value={form.pincode}
                disabled={!writable}
                onChange={(e) => set("pincode", e.target.value)}
              />
            </Field>
            <Field label="Country">
              <input
                className={inputCls}
                value={form.country}
                disabled={!writable}
                onChange={(e) => set("country", e.target.value)}
              />
            </Field>
            <Field label="Latitude">
              <input
                data-testid="land-lat-input"
                className={inputCls}
                placeholder="e.g., 19.8762"
                value={form.latitude}
                disabled={!writable}
                onChange={(e) => set("latitude", e.target.value)}
              />
            </Field>
            <Field label="Longitude">
              <input
                data-testid="land-lng-input"
                className={inputCls}
                placeholder="e.g., 75.3433"
                value={form.longitude}
                disabled={!writable}
                onChange={(e) => set("longitude", e.target.value)}
              />
            </Field>
            <Field label="Total Area">
              <input
                data-testid="land-area-input"
                type="number"
                className={inputCls}
                value={form.total_area}
                disabled={!writable}
                onChange={(e) => set("total_area", e.target.value)}
              />
            </Field>
            <Field label="Area Unit">
              <select
                data-testid="land-area-unit"
                className={inputCls}
                value={form.area_unit}
                disabled={!writable}
                onChange={(e) => set("area_unit", e.target.value)}
              >
                {AREA_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Ownership Type">
              <select
                data-testid="land-ownership"
                className={inputCls}
                value={form.ownership_type}
                disabled={!writable}
                onChange={(e) => set("ownership_type", e.target.value)}
              >
                {OWNERSHIP_TYPES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Purchase Date">
              <input
                type="date"
                className={inputCls}
                value={form.purchase_date}
                disabled={!writable}
                onChange={(e) => set("purchase_date", e.target.value)}
              />
            </Field>
            <Field label="Purchase Value (INR)" full>
              <input
                type="number"
                className={inputCls}
                value={form.purchase_value}
                disabled={!writable}
                onChange={(e) => set("purchase_value", e.target.value)}
              />
            </Field>
            <Field label="Notes" full>
              <textarea
                className={inputCls}
                rows={2}
                value={form.notes}
                disabled={!writable}
                onChange={(e) => set("notes", e.target.value)}
              />
            </Field>
            <div className="md:col-span-2">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-3">
                Land Documents
              </div>
              <DocumentList
                docs={form.documents}
                onChange={(docs) => set("documents", docs)}
                writable={writable}
              />
            </div>
          </div>
          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            {writable && (
              <button
                onClick={save}
                disabled={saving}
                data-testid="land-save"
                className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
              >
                {saving ? "Saving…" : editing ? "Update" : "Create"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Remove this land?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.name} will be removed permanently. This is blocked if it
              has properties or active allocations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
